import { matchesActiveCompany, matchesCompany } from '../../../../util/user/companySwitch';
import { isProjectFavourite } from './projectPageUtils';

export const DEFAULT_PROJECT_FILTERS = {
	searchQuery: '',
	typeFilter: 'all',
	statusFilter: 'all',
	customerFilter: 'all',
	jobNumberFilter: '',
	operatingAreaFilter: 'all',
	surveyTypeFilter: 'all',
	dateFilter: 'all',
	dateFrom: '',
	dateTo: '',
	sortBy: 'recent'
};

export const DATE_FILTER_OPTIONS = [
	{ value: 'day', label: 'Last 24 Hours' },
	{ value: 'week', label: 'Last 7 Days' },
	{ value: 'month', label: 'Last Month' },
	{ value: 'year', label: 'Last Year' },
	{ value: 'custom', label: 'Custom Range' }
];

export const SURVEY_TYPE_FILTER_LABELS = {
	ROW: 'XLI ROW',
	ON_OFF: 'XLI On/Off',
	ON: 'XLI On',
	'ON-OFF': 'XLI On/Off',
	DEPOL: 'Depolarization',
	DCVG: 'DCVG Survey',
	ACVG: 'ACVG Survey',
	REL: 'REL Survey',
	DOC: 'DOC Inspection',
	REPORTED_ANOMALIES: 'Anomaly Report',
	NATIVE: 'Annual CP',
	ACCA: 'Annual CP (ACCA)',
	ACPSP: 'Annual CP (ACPSP)',
	PROFILE: 'Profile Survey',
	UNKNOWN: 'XLI Survey',
	CP_SURVEY: 'Cathodic Protection',
	TRACKING: 'Pig Tracking'
};

// NOTE: DOC / REPORTED_ANOMALIES / NATIVE / ACCA / ACPSP / PROFILE are XLI
// survey sub-types (per the deficiency-automation project) and are now
// grouped under the 'overline' (Spectrum XLI) category below so they are
// recognised as Spectrum XLI projects for filtering / isSpectrumXliLine,
// even though ProjectCard/ProjectSummaryContent/SummaryModal keep their own
// distinct badge colours (see SURVEY_TYPE_CONFIG in those files).
export const SURVEY_TYPE_CATEGORY = {
	ROW: 'overline',
	ON_OFF: 'overline',
	ON: 'overline',
	'ON-OFF': 'overline',
	DEPOL: 'overline',
	DCVG: 'overline',
	ACVG: 'overline',
	REL: 'overline',
	DOC: 'overline',
	REPORTED_ANOMALIES: 'overline',
	NATIVE: 'overline',
	ACCA: 'overline',
	ACPSP: 'overline',
	PROFILE: 'overline',
	UNKNOWN: 'overline',
	CP_SURVEY: 'cp-survey',
	TRACKING: 'tracking'
};

export const PROJECT_TYPE_OPTIONS = [
	{ value: 'cp-survey', label: 'Cathodic Protection' },
	{ value: 'overline', label: 'Spectrum XLI' },
	{ value: 'tracking', label: 'Armadillo Tracking' }
];

export const ALL_PROJECTS_FILTERS_STORAGE_KEY = 'cg_all_projects_filters';
export const MY_PROJECTS_FILTERS_STORAGE_KEY = 'cg_my_projects_filters';

export const loadStoredProjectFilters = storageKey => {
	try {
		const parsed = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
		if (['new', 'active'].includes(parsed.statusFilter)) {
			parsed.statusFilter = 'in-progress';
		} else if (parsed.statusFilter === 'archived') {
			parsed.statusFilter = 'complete';
		}
		return Object.keys(DEFAULT_PROJECT_FILTERS).reduce((acc, key) => {
			if (key in parsed) {acc[key] = parsed[key];}
			return acc;
		}, {});
	} catch {
		return {};
	}
};

export const persistStoredProjectFilters = (storageKey, filters) => {
	try {
		const toStore = {};
		Object.keys(DEFAULT_PROJECT_FILTERS).forEach(key => { toStore[key] = filters[key]; });
		window.sessionStorage.setItem(storageKey, JSON.stringify(toStore));
	} catch {
		// ignore
	}
};

export const PROJECT_STATUS_OPTIONS = [
	{
		value: 'in-progress',
		label: 'In Progress',
		keys: ['NEW', 'ACTIVE', 'IN_PROGRESS', 'INPROGRESS', 'SURVEY ONGOING']
	},
	{ value: 'reporting', label: 'Reporting', keys: ['PROCESSING', 'REPORTING'] },
	{ value: 'complete', label: 'Complete', keys: ['COMPLETE', 'COMPLETED', 'ARCHIVED'] }
];

export const getLineOperatingArea = line => {
	const top = line.operatingArea || line.operatingAreaName;
	if (top && top !== 'not set' && String(top).trim() !== '') {return String(top).trim();}

	const jobs = line.jobs || [];
	const primaryId = line.primarySurvey && line.primarySurvey.jobId;
	const job = primaryId ? jobs.find(item => item.id === primaryId) : jobs[0];
	const entity = job && job.entity;

	return entity && entity !== 'not set' && String(entity).trim() !== ''
		? String(entity).trim()
		: null;
};

export const matchesDateFilter = (rawDate, dateFilter, dateFrom, dateTo) => {
	if (dateFilter === 'all') {return true;}
	if (!rawDate) {return false;}
	const date = new Date(rawDate);
	const now = new Date();

	if (dateFilter === 'day') {
		return date >= new Date(now.getTime() - 24 * 60 * 60 * 1000);
	}
	if (dateFilter === 'week') {
		return date >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
	}
	if (dateFilter === 'month') {
		const cutoff = new Date(now);
		cutoff.setMonth(cutoff.getMonth() - 1);
		return date >= cutoff;
	}
	if (dateFilter === 'year') {
		const cutoff = new Date(now);
		cutoff.setFullYear(cutoff.getFullYear() - 1);
		return date >= cutoff;
	}
	if (dateFilter === 'custom') {
		if (dateFrom && date < new Date(dateFrom)) {return false;}
		if (dateTo) {
			const toDate = new Date(dateTo);
			toDate.setHours(23, 59, 59, 999);
			if (date > toDate) {return false;}
		}
	}
	return true;
};

// The company a project card belongs to.  A Tracking run is owned by its
// pipeline's operating company (pipeline.company), never run.customer.
export const getLineCustomer = line => {
	if (!line) {return '';}
	if (line.source === 'tracking') {
		return (line.pipeline && line.pipeline.company) || '';
	}
	return line.customer || '';
};

export const applyProjectFiltersAndSort = (lines, filters, favourites = new Set()) => {
	const {
		searchQuery,
		typeFilter,
		statusFilter,
		customerFilter,
		jobNumberFilter,
		operatingAreaFilter,
		surveyTypeFilter,
		dateFilter,
		dateFrom,
		dateTo,
		sortBy
	} = filters;

	// When an operator has switched into a company, the whole app is scoped to
	// that one company — everything outside it is hidden before any other filter.
	const scopedLines = lines.filter(line => matchesActiveCompany(getLineCustomer(line)));

	const filtered = scopedLines.filter(line => {
		const surveyType = ((line.primarySurvey && line.primarySurvey.type) || '').toUpperCase();
		const surveyStatus = ((line.primarySurvey && line.primarySurvey.status) || '').toUpperCase();
		const lineCustomer = getLineCustomer(line).toLowerCase();
		const lineName = (line.name || '').toLowerCase();
		const opArea = (getLineOperatingArea(line) || '').toLowerCase();
		const facName = (line.facilityName || '').toLowerCase();
		const licNum = (line.lineLicenseNumber || '').toLowerCase();
		const jobStr = String(
			(line.primarySurvey && line.primarySurvey.strJobId) ||
			(line.primarySurvey && line.primarySurvey.jobId) ||
			''
		).toLowerCase();

		if (searchQuery) {
			const query = searchQuery.toLowerCase().trim();
			if (
				!lineName.includes(query) &&
				!jobStr.includes(query) &&
				!lineCustomer.includes(query) &&
				!opArea.includes(query) &&
				!facName.includes(query) &&
				!licNum.includes(query)
			) {
				return false;
			}
		}

		if (typeFilter !== 'all') {
			const category = getLineTypeCategory(line);
			if (category !== typeFilter) {return false;}
		}

		if (statusFilter !== 'all') {
			const option = PROJECT_STATUS_OPTIONS.find(o => o.value === statusFilter);
			if (option) {
				if (!option.keys.includes(surveyStatus)) {return false;}
			} else {
				const normalized = statusFilter.toUpperCase().replace(/-/g, '_');
				if (surveyStatus !== normalized) {return false;}
			}
		}

		if (customerFilter !== 'all' && !matchesCompany(getLineCustomer(line), customerFilter)) {
			return false;
		}

		if (jobNumberFilter && jobStr !== String(jobNumberFilter).trim().toLowerCase()) {
			return false;
		}

		if (operatingAreaFilter !== 'all' && opArea !== operatingAreaFilter.toLowerCase()) {
			return false;
		}

		if (surveyTypeFilter !== 'all') {
			const listTypes = (line.surveyTypesList || '')
				.split(/[\s,]+/)
				.map(type => type.trim().toUpperCase())
				.filter(Boolean);

			if (surveyType !== surveyTypeFilter && !listTypes.includes(surveyTypeFilter)) {
				return false;
			}
		}

		if (!matchesDateFilter(
			line.primarySurvey && line.primarySurvey.start,
			dateFilter,
			dateFrom,
			dateTo
		)) {
			return false;
		}

		return true;
	});

	return [...filtered].sort((a, b) => {
		const aFav = isProjectFavourite(favourites, a.id || a.name);
		const bFav = isProjectFavourite(favourites, b.id || b.name);

		if (sortBy === 'favourites' && aFav !== bFav) {
			return aFav ? -1 : 1;
		}

		if (sortBy === 'status') {
			const aStatus = ((a.primarySurvey && a.primarySurvey.status) || '').toUpperCase();
			const bStatus = ((b.primarySurvey && b.primarySurvey.status) || '').toUpperCase();
			if (aStatus !== bStatus) {
				// Lines without a status sort last; ties fall through to newest date
				if (!aStatus) {return 1;}
				if (!bStatus) {return -1;}
				return aStatus.localeCompare(bStatus);
			}
		}

		const aDate = (a.primarySurvey && a.primarySurvey.start) || new Date(0);
		const bDate = (b.primarySurvey && b.primarySurvey.start) || new Date(0);

		if (sortBy === 'oldest') {return aDate - bDate;}

		return bDate - aDate;
	});
};

export const hasActiveProjectFilters = filters => (
	filters.searchQuery !== '' ||
	filters.typeFilter !== 'all' ||
	filters.statusFilter !== 'all' ||
	filters.customerFilter !== 'all' ||
	(filters.jobNumberFilter || '') !== '' ||
	filters.operatingAreaFilter !== 'all' ||
	filters.surveyTypeFilter !== 'all' ||
	filters.dateFilter !== 'all'
);

export const countActiveProjectFilters = filters => (
	[
		filters.typeFilter !== 'all',
		filters.statusFilter !== 'all',
		filters.customerFilter !== 'all',
		(filters.jobNumberFilter || '') !== '',
		filters.operatingAreaFilter !== 'all',
		filters.surveyTypeFilter !== 'all',
		filters.dateFilter !== 'all'
	].filter(Boolean).length
);

export const getLoadedCustomers = (lines) => {
	const seen = new Set();
	const customers = [];
	lines.forEach(line => {
		const customer = getLineCustomer(line).trim();
		if (customer && !seen.has(customer.toLowerCase())) {
			seen.add(customer.toLowerCase());
			customers.push(customer);
		}
	});
	return customers.sort((a, b) => a.localeCompare(b));
};

// Short project-type initials shown before a company name in the Company filter,
// mirroring the CP / XLI badges on the CorrMonitor company filter.  Keyed by the
// map/legend project-type category (see SURVEY_TYPE_CATEGORY); ordered so the
// prefix reads consistently (e.g. "CP / XLI — Acme").
export const PROJECT_TYPE_INITIALS = [
	{ key: 'cp-survey', initials: 'CP' },
	{ key: 'overline', initials: 'XLI' },
	{ key: 'tracking', initials: 'ARM' },
	{ key: 'annual-cp', initials: 'ACP' },
	{ key: 'doc', initials: 'DOC' }
];

// Resolve a line to its project-type category — the same classification the All
// Projects map uses (source wins for CorrMonitor / Tracking, otherwise the
// survey type maps through SURVEY_TYPE_CATEGORY).
export const getLineTypeCategory = line => {
	if (!line) {return 'other';}
	if (line.source === 'corrmonitor') {return 'cp-survey';}
	if (line.source === 'tracking') {return 'tracking';}
	if (line.source === 'xli') {return 'overline';}
	const surveyType = ((line.primarySurvey && line.primarySurvey.type) || '').toUpperCase();
	return SURVEY_TYPE_CATEGORY[surveyType] || 'other';
};

// Build a map of lowercased company name → ordered project-type initials for the
// data that company has (e.g. { 'acme': ['CP', 'XLI'] }).  Powers the CorrMonitor
// style type prefix on each company row of the Company filter.
export const getCustomerTypeInitials = lines => {
	const categoriesByCustomer = new Map();
	lines.forEach(line => {
		const customer = getLineCustomer(line).trim();
		if (!customer) {return;}
		const key = customer.toLowerCase();
		if (!categoriesByCustomer.has(key)) {categoriesByCustomer.set(key, new Set());}
		categoriesByCustomer.get(key).add(getLineTypeCategory(line));
	});

	const result = {};
	categoriesByCustomer.forEach((categories, key) => {
		result[key] = PROJECT_TYPE_INITIALS
			.filter(item => categories.has(item.key))
			.map(item => item.initials);
	});
	return result;
};

export const getAvailableStatuses = lines => {
	const statuses = new Set();

	lines.forEach(line => {
		const status = ((line.primarySurvey && line.primarySurvey.status) || '').toUpperCase();
		if (status) {statuses.add(status);}
	});

	return statuses;
};

export const getAvailableOperatingAreas = lines => {
	const areas = new Set();

	lines.forEach(line => {
		const area = getLineOperatingArea(line);
		if (area) {areas.add(area);}
	});

	return [...areas].sort((a, b) => a.localeCompare(b));
};

export const getAvailableSurveyTypes = lines => {
	const seen = new Map();

	const addType = raw => {
		const key = (raw || '').trim().toUpperCase();
		if (!key || seen.has(key)) {return;}

		const label = SURVEY_TYPE_FILTER_LABELS[key]
			|| key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
		seen.set(key, label);
	};

	lines.forEach(line => {
		addType((line.primarySurvey && line.primarySurvey.type) || '');

		const list = line.surveyTypesList || '';
		if (list) {list.split(/[\s,]+/).forEach(addType);}
	});

	return [...seen.entries()]
		.map(([value, label]) => ({ value, label }))
		.sort((a, b) => a.label.localeCompare(b.label));
};

export const getProjectFilterOptions = (rawLines, { customerFilter = 'all' } = {}) => {
	// Scope the option lists to the active company so dropdowns never surface
	// values from other companies while an operator is switched in.
	const lines = rawLines.filter(line => matchesActiveCompany(getLineCustomer(line)));
	const availableStatuses = getAvailableStatuses(lines);
	const statusOptions = PROJECT_STATUS_OPTIONS.filter(option =>
		option.keys.some(key => availableStatuses.has(key))
	);

	// When a customer is selected, restrict dependent dropdowns to that
	// customer's lines so Operating Area (and Survey Type) only show values
	// that actually exist under that customer.
	const scopedLines = customerFilter === 'all'
		? lines
		: lines.filter(line =>
			getLineCustomer(line).toLowerCase() === customerFilter.toLowerCase()
		);

	return {
		availableOperatingAreas: getAvailableOperatingAreas(scopedLines),
		availableSurveyTypes: getAvailableSurveyTypes(scopedLines),
		loadedCustomers: getLoadedCustomers(lines), // always full set — never scoped
		customerTypeInitials: getCustomerTypeInitials(lines), // full set — never scoped
		statusOptions
	};
};
